/**
 * Time calculation utilities for Daily Time Records
 */

import { toYMDString } from './periodDate';

/**
 * Calculate working hours between two timestamps
 * @param {Date} timeIn - Time in as Date object
 * @param {Date} timeOut - Time out as Date object
 * @param {number} breakMinutes - Break duration in minutes
 * @returns {number} Hours worked (rounded to 2 decimals)
 */
export const calculateWorkingHours = (timeIn, timeOut, breakMinutes = 0) => {
  if (!timeIn || !timeOut) return 0;

  const msWorked = timeOut.getTime() - timeIn.getTime();
  const hoursWorked = (msWorked / (1000 * 60 * 60)) - (breakMinutes / 60);

  return Math.max(0, Math.round(hoursWorked * 100) / 100);
};

/**
 * Calculate undertime hours (hours short of regular hours)
 * @param {number} hoursWorked - Total hours worked
 * @param {number} hoursPerDay - Regular hours per day from payrollRates
 * @returns {number} Undertime hours (rounded to 2 decimals)
 * @throws {Error} If hoursPerDay is missing
 */
export const calculateUndertime = (hoursWorked, hoursPerDay) => {
  if (typeof hoursPerDay !== 'number') {
    throw new Error('Missing required hoursPerDay. Please configure in Settings > Pay Rates.');
  }
  return Math.max(0, Math.round((hoursPerDay - hoursWorked) * 100) / 100);
};

/**
 * Calculate effective time in with early clock-in leeway.
 * If employee clocks in within leeway window before scheduled start,
 * effective time in is clamped to scheduled start.
 *
 * @param {Date} actualTimeIn - Actual clock-in time
 * @param {Date} scheduledStart - Scheduled start time (Date object with correct calendar date)
 * @param {number} leewayEarlyTimeIn - Leeway minutes before scheduled start
 * @param {number} leewayLateGracePeriod - Grace minutes after scheduled start (minor lateness forgiven)
 * @returns {Date} Effective time in (clamped or actual)
 * @throws {Error} If required params are missing
 */
export const calculateEffectiveTimeIn = (actualTimeIn, scheduledStart, leewayEarlyTimeIn, leewayLateGracePeriod) => {
  if (!actualTimeIn || !scheduledStart) {
    throw new Error('calculateEffectiveTimeIn requires actualTimeIn and scheduledStart');
  }
  if (typeof leewayEarlyTimeIn !== 'number') {
    throw new Error('Missing required leewayEarlyTimeIn. Please configure in Settings > Attendance Policy.');
  }
  if (typeof leewayLateGracePeriod !== 'number') {
    throw new Error('Missing required leewayLateGracePeriod. Please configure in Settings > Attendance Policy.');
  }

  const leewayWindowStart = new Date(scheduledStart.getTime() - leewayEarlyTimeIn * 60000);
  const lateGraceEnd = new Date(scheduledStart.getTime() + leewayLateGracePeriod * 60000);

  // Clamp if employee arrived within the full window:
  // [scheduledStart - earlyLeeway, scheduledStart + lateGrace]
  // Early arrivals within leeway → no inflated hours
  // Minor late arrivals within grace → no undertime
  if (actualTimeIn >= leewayWindowStart && actualTimeIn <= lateGraceEnd) {
    return new Date(scheduledStart);
  }

  // Outside window (unusually early OR late beyond grace) — use actual time
  return new Date(actualTimeIn);
};

/**
 * Calculate effective time out with OT start leeway.
 * If employee clocks out within leeway window after scheduled end,
 * effective time out is clamped to scheduled end.
 *
 * @param {Date} actualTimeOut - Actual clock-out time
 * @param {Date} scheduledEnd - Scheduled end time (Date object with correct calendar date)
 * @param {number} leewayOtStart - Leeway minutes after scheduled end before OT counts
 * @param {number} leewayUndertimeGracePeriod - Grace minutes before scheduled end (minor early departures forgiven)
 * @returns {Date} Effective time out (clamped or actual)
 * @throws {Error} If required params are missing
 */
export const calculateEffectiveTimeOut = (actualTimeOut, scheduledEnd, leewayOtStart, leewayUndertimeGracePeriod) => {
  if (!actualTimeOut || !scheduledEnd) {
    throw new Error('calculateEffectiveTimeOut requires actualTimeOut and scheduledEnd');
  }
  if (typeof leewayOtStart !== 'number') {
    throw new Error('Missing required leewayOtStart. Please configure in Settings > Attendance Policy.');
  }
  if (typeof leewayUndertimeGracePeriod !== 'number') {
    throw new Error('Missing required leewayUndertimeGracePeriod. Please configure in Settings > Attendance Policy.');
  }

  const undertimeGraceStart = new Date(scheduledEnd.getTime() - leewayUndertimeGracePeriod * 60000);
  const otThreshold = new Date(scheduledEnd.getTime() + leewayOtStart * 60000);

  // Clamp if employee left within the full window:
  // [scheduledEnd - undertimeGrace, scheduledEnd + otLeeway]
  // Minor early departures within grace → no undertime
  // Minor overstays within leeway → no OT
  if (actualTimeOut >= undertimeGraceStart && actualTimeOut <= otThreshold) {
    return new Date(scheduledEnd);
  }

  // Outside window (left too early OR stayed past OT leeway) — use actual time
  return new Date(actualTimeOut);
};

/**
 * Calculate overtime hours for rest day work (no schedule, no leeway).
 * Simple hours-based: OT = hoursWorked - hoursPerDay.
 *
 * @param {number} hoursWorked - Total hours worked
 * @param {number} hoursPerDay - Regular hours per day from payrollRates
 * @returns {number} Overtime hours (rounded to 2 decimals)
 * @throws {Error} If hoursPerDay is missing
 */
export const calculateOvertimeRestDay = (hoursWorked, hoursPerDay) => {
  if (typeof hoursPerDay !== 'number') {
    throw new Error('Missing required hoursPerDay. Please configure in Settings > Pay Rates.');
  }
  return Math.max(0, Math.round((hoursWorked - hoursPerDay) * 100) / 100);
};

/**
 * Calculate late hours: how far after scheduled start the employee effectively clocked in.
 * Grace is already absorbed by effectiveTimeIn (calculateEffectiveTimeIn clamps within-grace
 * arrivals to scheduledStart), so this returns 0 within grace and full lateness past it.
 * Informational only — never deducted.
 *
 * @param {Date} effectiveTimeIn - Effective clock-in time (after leeway)
 * @param {Date} scheduledStart - Scheduled start time
 * @returns {number} Late hours (rounded to 2 decimals); 0 if either arg is missing
 */
export const calculateLateHours = (effectiveTimeIn, scheduledStart) => {
  if (!effectiveTimeIn || !scheduledStart) return 0;
  const lateMs = effectiveTimeIn.getTime() - scheduledStart.getTime();
  return Math.max(0, Math.round((lateMs / (1000 * 60 * 60)) * 100) / 100);
};

/**
 * Apply OT increment (floor rounding) to overtime hours.
 * OT is paid in blocks of `incrementMinutes`. Partial blocks are dropped.
 *
 * @param {number} overtimeHours - Raw overtime hours
 * @param {number} incrementMinutes - Increment size in minutes (0, 15, 30). 0 = off (no rounding).
 * @returns {number} Floored overtime hours (rounded to 2 decimals)
 */
export const applyOtIncrement = (overtimeHours, incrementMinutes = 0) => {
  if (!overtimeHours || overtimeHours <= 0) return 0;
  if (!incrementMinutes || incrementMinutes <= 0) return overtimeHours;

  const rawMinutes = overtimeHours * 60;
  const flooredMinutes = Math.floor(rawMinutes / incrementMinutes) * incrementMinutes;
  return Math.round((flooredMinutes / 60) * 100) / 100;
};

/**
 * Build a Date object from a date string and a 24-hour time string.
 * Used to construct scheduledStart/scheduledEnd from employee work schedule.
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} time24 - Time in HH:MM 24-hour format
 * @returns {Date} Combined date and time
 */
export const buildScheduleDateTime = (dateStr, time24) => {
  // Manila wall time, whatever the browser's zone (PRD_TimeRecordAndPayFixes C3).
  return new Date(`${dateStr}T${time24}:00+08:00`);
};

/**
 * Hours of [timeIn, timeOut) that fall inside the night-diff window, cross-midnight
 * aware. Steps the shift by one minute and classifies each minute by its hour, so
 * off-the-hour clock-in/out crossing the window boundary is counted exactly.
 *
 * @param {Date} timeIn - Time in as Date object
 * @param {Date} timeOut - Time out as Date object
 * @param {number} nightDiffStart - Hour when ND starts (e.g. 22 for 10PM)
 * @param {number} nightDiffEnd - Hour when ND ends (e.g. 6 for 6AM)
 * @returns {number} Raw night hours (unrounded)
 */
const MANILA_OFFSET_MS = 8 * 60 * 60 * 1000;

const nightOverlapHours = (timeIn, timeOut, nightDiffStart, nightDiffEnd) => {
  const startTime = new Date(timeIn);
  const endTime = new Date(timeOut);

  // Handle cross-day shift
  if (endTime <= startTime) {
    endTime.setDate(endTime.getDate() + 1);
  }

  let minutes = 0;
  let current = new Date(startTime);
  while (current < endTime) {
    // The Manila hour of this minute (UTC+8, no DST) — not the browser's (C3).
    const hour = new Date(current.getTime() + MANILA_OFFSET_MS).getUTCHours();
    const next = new Date(current.getTime() + 60000); // Add 1 minute

    // Check if the current minute falls in the night diff period
    if (hour >= nightDiffStart || hour < nightDiffEnd) {
      const periodEnd = next > endTime ? endTime : next;
      minutes += (periodEnd - current) / 60000;
    }

    current = next;
  }

  return minutes / 60;
};

/**
 * Calculate night differential hours within a configurable ND window.
 * Break time is assumed to be taken during non-ND hours first.
 * Only if break exceeds non-ND time, the remainder is deducted from ND.
 *
 * @param {Date} timeIn - Time in as Date object
 * @param {Date} timeOut - Time out as Date object
 * @param {Object} options - Required options object
 * @param {number} options.breakMinutes - Break duration in minutes
 * @param {number} options.nightDiffStart - Hour when ND starts (from payrollRates)
 * @param {number} options.nightDiffEnd - Hour when ND ends (from payrollRates)
 * @returns {number} Night differential hours (rounded to 2 decimals)
 * @throws {Error} If options are missing or invalid
 */
export const calculateNightDiff = (timeIn, timeOut, options) => {
  if (!timeIn || !timeOut) return 0;

  // Validate required options
  if (!options || typeof options !== 'object') {
    throw new Error('calculateNightDiff requires options object with breakMinutes, nightDiffStart, and nightDiffEnd from attendancePolicy');
  }

  const { breakMinutes, nightDiffStart, nightDiffEnd } = options;

  if (typeof breakMinutes !== 'number' || typeof nightDiffStart !== 'number' || typeof nightDiffEnd !== 'number') {
    throw new Error('Missing required attendance policy values (nightDiffStart, nightDiffEnd). Please configure these in Settings > Attendance Policy.');
  }

  const startTime = new Date(timeIn);
  const endTime = new Date(timeOut);

  // Handle cross-day shift
  if (endTime <= startTime) {
    endTime.setDate(endTime.getDate() + 1);
  }

  // Calculate total shift duration in hours
  const totalShiftHours = (endTime - startTime) / (1000 * 60 * 60);
  if (totalShiftHours <= 0) return 0;

  // Raw ND hours — per-minute count, exact at off-the-hour boundary crossings.
  const rawNightHours = nightOverlapHours(timeIn, timeOut, nightDiffStart, nightDiffEnd);

  // Calculate non-ND hours in the shift
  const nonNightHours = totalShiftHours - rawNightHours;

  // Break is assumed to be taken during non-ND time first
  const breakHours = breakMinutes / 60;
  const breakAbsorbedByNonND = Math.min(breakHours, nonNightHours);
  const breakToDeductFromND = breakHours - breakAbsorbedByNonND;

  // ND hours = raw ND - any break portion not absorbed by non-ND time
  const adjustedNightHours = Math.max(0, rawNightHours - breakToDeductFromND);

  return Math.round(adjustedNightHours * 100) / 100;
};

/**
 * Convert 12-hour time format to 24-hour format
 * @param {string} time - Time in HH:MM format
 * @param {string} period - AM or PM
 * @returns {string} Time in 24-hour HH:MM format
 */
export const to24Hour = (time, period) => {
  const [hours, minutes] = time.split(':').map(Number);
  let hour24 = hours;

  if (period === 'PM' && hours !== 12) {
    hour24 = hours + 12;
  } else if (period === 'AM' && hours === 12) {
    hour24 = 0;
  }

  return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Convert 24-hour time format to 12-hour format
 * @param {string} time24 - Time in 24-hour HH:MM format
 * @returns {object} { time: 'HH:MM', period: 'AM'|'PM' }
 */
export const to12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return {
    time: `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
    period
  };
};

/**
 * Create a Date object from date string and time components
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format
 * @param {string} period - AM or PM
 * @returns {Date} Combined date and time
 */
export const createDateTime = (dateStr, time, period) => {
  const time24 = to24Hour(time, period);
  // Manila wall time, whatever the browser's zone (PRD_TimeRecordAndPayFixes C3,
  // BL-079) — the instant an operator in Melbourne enters is the same one the
  // kiosk would stamp in Manila.
  return new Date(`${dateStr}T${time24}:00+08:00`);
};

/**
 * Validate if time out is after time in
 * @param {Date} timeIn - Time in as Date object
 * @param {Date} timeOut - Time out as Date object
 * @returns {boolean} True if valid
 */
export const isValidTimeRange = (timeIn, timeOut) => {
  if (!timeIn || !timeOut) return true; // Allow partial records
  return timeOut > timeIn;
};

/**
 * Check if a date is within the allowed range (max 3 months ago)
 * @param {Date} date - Date to check
 * @returns {boolean} True if within allowed range
 */
export const isDateWithinAllowedRange = (date) => {
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return date >= threeMonthsAgo;
};

/**
 * Format date for display — 'YYYY-MM-DD', the app-wide standard
 * (DESIGN-STANDARDS.md §13). Was 'Oct 1, 2025'.
 *
 * Every caller renders a bare value-date: AddTimeRecordModal's read-only Date
 * field and the four date <option> labels in its overnight-shift picker.
 * @param {Date|string} date - Date to format
 * @returns {string} e.g. "2025-10-01"
 */
export const formatDisplayDate = (date) => toYMDString(date);

/**
 * Format date to YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateString = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string in YYYY-MM-DD format
 * @param {string} dateStr - Date string
 * @returns {Date} Date object
 */
export const parseDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Check if a shift has Night Differential break ambiguity.
 * This occurs when a shift spans both ND and non-ND periods with a break,
 * making it unclear when the break was taken.
 *
 * @param {Date} timeIn - Time in as Date object
 * @param {Date} timeOut - Time out as Date object
 * @param {Object} options - Options object
 * @param {number} options.breakMinutes - Break duration in minutes
 * @param {number} options.nightDiffStart - Hour when ND starts (e.g., 22 for 10PM)
 * @param {number} options.nightDiffEnd - Hour when ND ends (e.g., 6 for 6AM)
 * @returns {boolean} True if there's ambiguity about break timing
 */
export const hasNDBreakAmbiguity = (timeIn, timeOut, options) => {
  if (!timeIn || !timeOut) return false;
  if (!options || typeof options !== 'object') return false;

  const { breakMinutes, nightDiffStart, nightDiffEnd } = options;

  // No ambiguity if no break
  if (!breakMinutes || breakMinutes <= 0) return false;

  // Validate ND config
  if (typeof nightDiffStart !== 'number' || typeof nightDiffEnd !== 'number') return false;

  const startTime = new Date(timeIn);
  const endTime = new Date(timeOut);

  // Handle cross-day shift
  if (endTime <= startTime) {
    endTime.setDate(endTime.getDate() + 1);
  }

  // Calculate total shift duration in hours
  const totalShiftHours = (endTime - startTime) / (1000 * 60 * 60);
  if (totalShiftHours <= 0) return false;

  // Raw ND hours — per-minute count, exact at off-the-hour boundary crossings.
  const rawNightHours = nightOverlapHours(timeIn, timeOut, nightDiffStart, nightDiffEnd);

  // Calculate non-ND hours
  const nonNightHours = totalShiftHours - rawNightHours;

  // Ambiguity exists when shift has both ND and non-ND hours with a break
  return rawNightHours > 0 && nonNightHours > 0;
};